import React, { useState } from 'react';
import { Button, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import SingleSelect from 'components/SingleSelect';
import { WarehousePlanningEntryConfigFiltersInput } from '../graphql/queries/getWarehousePlanningEntryConfigs';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

interface WarehousePlanningEntryConfigFilterProps {
    onFilter: (filters: WarehousePlanningEntryConfigFiltersInput) => void;
    warehouseOptions: { label: string; value: number }[];
    entryNodeOptions: { label: string; value: string }[];
    isActiveOptions: { label: string; value: boolean }[];
    onCreate?: () => void;
}

const WarehousePlanningEntryConfigFilter: React.FC<WarehousePlanningEntryConfigFilterProps> = ({
    onFilter,
    warehouseOptions,
    entryNodeOptions,
    isActiveOptions,
    onCreate
}) => {
    const { t } = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    // 过滤条件
    const [filters, setFilters] = useState<WarehousePlanningEntryConfigFiltersInput>({});

    const labelWarehouse = String(t('config.entryConfig.filter.warehouse'));
    const labelEntryNode = String(t('config.entryConfig.filter.entryNode'));
    const labelIsActive = String(t('config.entryConfig.filter.isActive'));
    const col1Labels = [labelWarehouse];
    const col2Labels = [labelEntryNode];
    const col3Labels = [labelIsActive];

    // 查询
    const applyFilters = () => {
        onFilter({ ...filters });
    };

    // 重置
    const resetFilters = () => {
        const emptyFilters: WarehousePlanningEntryConfigFiltersInput = {};
        setFilters(emptyFilters);
        onFilter(emptyFilters);
    };

    // 添加筛选条件，并传给配置页。
    const handleFilterChange = (name: keyof WarehousePlanningEntryConfigFiltersInput, value: any) => {
        setFilters(prev => ({
            ...prev,                    // 保留其他字段的值
            [name]: value === null || value === undefined || value === '' ? undefined : value  // 更新指定字段，保留 false 值
        }));
    };

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <SingleSelect
                        label={labelWarehouse}
                        value={filters.warehouseId !== undefined ? String(filters.warehouseId) : undefined}
                        onChange={value => {
                            const numValue = value === '' ? undefined : Number(value);
                            handleFilterChange('warehouseId', numValue);
                        }}
                        options={warehouseOptions.map(opt => ({
                            label: opt.label,
                            value: String(opt.value)
                        }))}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelEntryNode}
                        value={filters.entryNode}
                        onChange={value => handleFilterChange('entryNode', value === '' ? undefined : value)}
                        options={entryNodeOptions}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(col3Labels)}>{labelIsActive}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                value={filters.isActive}
                                onChange={(value) => handleFilterChange('isActive', value)}
                                options={isActiveOptions}
                                allowClear
                                placeholder={t('config.entryConfig.filter.all')}
                                className="w-full"
                                size="middle"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-3 mb-1 flex justify-between items-center pl-0">
                <div className="flex gap-4">
                    {onCreate && (
                        <Button
                            type="primary"
                            onClick={onCreate}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('config.entryConfig.buttons.create')}</span>
                        </Button>
                    )}
                </div>
                <div className="flex gap-4">
                    <Button
                        type="primary"
                        onClick={applyFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('config.entryConfig.buttons.search')}</span>
                    </Button>
                    <Button
                        type="default"
                        onClick={resetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('config.entryConfig.buttons.reset')}</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default WarehousePlanningEntryConfigFilter;

